// ═══════════════════════════════════════════════════════════════════════════════
// DEAL INTELLIGENCE ENGINE v1.0
// EnergiPro Solutions — Client Activity Tracking & Deal Health Scoring
// ═══════════════════════════════════════════════════════════════════════════════
// Signals: Email opens, Portal access, Revision requests, Response time, Inactivity
// Health Score: 0–100 → Hot (80+) / Warm (50–79) / Cold (<50)
// Automated Actions: Hot → Alert + Call rec | Cold → Re-engage + Flag At Risk
// ═══════════════════════════════════════════════════════════════════════════════

// ─── TYPES ───────────────────────────────────────────────────────────────────

export type DealTemperature = 'hot' | 'warm' | 'cold';

export type ActivityType =
  | 'email_opened'
  | 'portal_visit'
  | 'button_click'
  | 'revision_request'
  | 'document_download'
  | 'response_sent'
  | 'proposal_viewed';

export type InterventionType = 'call_client' | 'send_custom_email' | 'escalate';

export interface ClientActivity {
  id: string;
  rfqId: string;
  type: ActivityType;
  timestamp: string; // ISO date
  details?: string;
  source?: string; // 'email' | 'portal' | 'api'
}

export interface DealHealthInput {
  rfqId: string;
  rfqScore: number; // From RFQ scoring engine (0-100)
  activities: ClientActivity[];
  proposalSentDate: string; // ISO date
  lastResponseDate?: string; // ISO when client last responded
  daysWithoutResponse: number;
}

export interface DealHealthResult {
  score: number; // 0-100
  temperature: DealTemperature;
  breakdown: DealHealthBreakdown;
  signals: DealSignal[];
  recommendation: string;
  automatedActions: AutomatedAction[];
  lastActivityDate: string | null;
  activityFrequency: number; // Activities per week
}

export interface DealHealthBreakdown {
  engagementScore: number;    // Email opens, portal visits (max 40)
  responseScore: number;      // Response timeliness (max 30)
  intentScore: number;        // Revision requests, doc downloads (max 20)
  baseScore: number;          // From RFQ priority score (max 10)
  decayPenalty: number;       // Negative for inactivity
}

export interface DealSignal {
  type: 'positive' | 'negative' | 'neutral';
  label: string;
  impact: number; // Points added/removed
  timestamp?: string;
}

export interface AutomatedAction {
  type: 'alert' | 're_engage' | 'flag_at_risk' | 'recommend_call' | 'escalate';
  label: string;
  description: string;
  urgency: 'immediate' | 'soon' | 'scheduled';
  triggered: boolean;
}

export interface InterventionRecord {
  id: string;
  rfqId: string;
  type: InterventionType;
  performedBy: string;
  performedAt: string; // ISO date
  notes?: string;
  outcome?: string;
}

export interface DealIntelligenceRecord {
  rfqId: string;
  referenceId: string;
  clientName: string;
  clientEmail: string;
  companyName: string;
  serviceName: string;
  proposalSentDate: string;
  rfqScore: number;
  dealHealthScore: number;
  temperature: DealTemperature;
  lastActivityDate: string | null;
  lastActivityType: ActivityType | null;
  activityFrequency: number;
  activities: ClientActivity[];
  interventions: InterventionRecord[];
  signals: DealSignal[];
  automatedActions: AutomatedAction[];
  recommendation: string;
  daysWithoutResponse: number;
  status: 'active' | 'won' | 'lost' | 'stale';
}

// ─── SCORING CONSTANTS ───────────────────────────────────────────────────────

const ACTIVITY_POINTS: Record<ActivityType, number> = {
  email_opened: 10,
  portal_visit: 15,
  button_click: 8,
  revision_request: 25,
  document_download: 12,
  response_sent: 20,
  proposal_viewed: 15,
};

const DECAY_THRESHOLDS = [
  { days: 3, penalty: 0 },
  { days: 5, penalty: -20 },
  { days: 7, penalty: -30 },
  { days: 10, penalty: -40 },
  { days: 14, penalty: -50 },
];

// ─── SCORING ENGINE ──────────────────────────────────────────────────────────

export function calculateDealHealth(input: DealHealthInput): DealHealthResult {
  const { activities, rfqScore, daysWithoutResponse } = input;

  // 1. Engagement Score (max 40) — email opens + portal visits
  const engagementActivities = activities.filter(
    a => a.type === 'email_opened' || a.type === 'portal_visit' || a.type === 'proposal_viewed'
  );
  const rawEngagement = engagementActivities.reduce((sum, a) => sum + ACTIVITY_POINTS[a.type], 0);
  const engagementScore = Math.min(40, rawEngagement);

  // 2. Response Score (max 30) — timeliness of client responses
  let responseScore = 0;
  const hasResponse = activities.some(a => a.type === 'response_sent');
  if (hasResponse) {
    // Check how fast they responded
    const responseDays = getResponseTimeDays(input);
    if (responseDays <= 1) responseScore = 30;
    else if (responseDays <= 2) responseScore = 25;
    else if (responseDays <= 3) responseScore = 20;
    else if (responseDays <= 5) responseScore = 15;
    else responseScore = 10;
  } else if (daysWithoutResponse <= 2) {
    responseScore = 15; // Still within acceptable window
  }

  // 3. Intent Score (max 20) — revision requests, doc downloads, button clicks
  const intentActivities = activities.filter(
    a => a.type === 'revision_request' || a.type === 'document_download' || a.type === 'button_click'
  );
  const rawIntent = intentActivities.reduce((sum, a) => sum + ACTIVITY_POINTS[a.type], 0);
  const intentScore = Math.min(20, rawIntent);

  // 4. Base Score (max 10) — from RFQ priority score (scaled from 0-100 to 0-10)
  const baseScore = Math.round((rfqScore / 100) * 10);

  // 5. Decay Penalty — inactivity penalty
  let decayPenalty = 0;
  for (const threshold of DECAY_THRESHOLDS) {
    if (daysWithoutResponse >= threshold.days) {
      decayPenalty = threshold.penalty;
    }
  }

  // Calculate final score
  const rawScore = engagementScore + responseScore + intentScore + baseScore + decayPenalty;
  const score = Math.max(0, Math.min(100, rawScore));

  // Classify temperature
  const temperature = classifyTemperature(score);

  // Generate signals
  const signals = generateSignals(activities, daysWithoutResponse, input);

  // Generate automated actions
  const automatedActions = generateAutomatedActions(temperature, daysWithoutResponse, activities);

  // Activity frequency (per week)
  const activityFrequency = calculateActivityFrequency(activities);

  // Last activity
  const lastActivity = activities.length > 0
    ? activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
    : null;

  // Recommendation
  const recommendation = generateRecommendation(temperature, daysWithoutResponse, activities);

  return {
    score,
    temperature,
    breakdown: { engagementScore, responseScore, intentScore, baseScore, decayPenalty },
    signals,
    recommendation,
    automatedActions,
    lastActivityDate: lastActivity?.timestamp || null,
    activityFrequency,
  };
}

// ─── CLASSIFICATION ──────────────────────────────────────────────────────────

export function classifyTemperature(score: number): DealTemperature {
  if (score >= 80) return 'hot';
  if (score >= 50) return 'warm';
  return 'cold';
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function getResponseTimeDays(input: DealHealthInput): number {
  if (!input.lastResponseDate) return 999;
  const proposalDate = new Date(input.proposalSentDate);
  const responseDate = new Date(input.lastResponseDate);
  const diffMs = responseDate.getTime() - proposalDate.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

function calculateActivityFrequency(activities: ClientActivity[]): number {
  if (activities.length < 2) return activities.length;
  const sorted = [...activities].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  const firstDate = new Date(sorted[0].timestamp);
  const lastDate = new Date(sorted[sorted.length - 1].timestamp);
  const weeks = Math.max(1, (lastDate.getTime() - firstDate.getTime()) / (7 * 24 * 60 * 60 * 1000));
  return Math.round((activities.length / weeks) * 10) / 10;
}

function generateSignals(activities: ClientActivity[], daysWithoutResponse: number, input: DealHealthInput): DealSignal[] {
  const signals: DealSignal[] = [];

  // Positive signals
  const emailOpens = activities.filter(a => a.type === 'email_opened').length;
  if (emailOpens > 0) {
    signals.push({ type: 'positive', label: `Email opened ${emailOpens}x`, impact: emailOpens * 10 });
  }

  const portalVisits = activities.filter(a => a.type === 'portal_visit').length;
  if (portalVisits > 0) {
    signals.push({ type: 'positive', label: `Portal accessed ${portalVisits}x`, impact: portalVisits * 15 });
  }

  const revisions = activities.filter(a => a.type === 'revision_request').length;
  if (revisions > 0) {
    signals.push({ type: 'positive', label: `Revision requested ${revisions}x`, impact: revisions * 25 });
  }

  const hasResponse = activities.some(a => a.type === 'response_sent');
  if (hasResponse) {
    const respDays = getResponseTimeDays(input);
    if (respDays <= 2) {
      signals.push({ type: 'positive', label: `Quick response (within ${respDays}d)`, impact: 20 });
    }
  }

  const docDownloads = activities.filter(a => a.type === 'document_download').length;
  if (docDownloads > 0) {
    signals.push({ type: 'positive', label: `${docDownloads} document(s) downloaded`, impact: docDownloads * 12 });
  }

  // Negative signals
  if (daysWithoutResponse >= 5) {
    signals.push({ type: 'negative', label: `No response for ${daysWithoutResponse} days`, impact: -20 });
  }

  if (daysWithoutResponse >= 10) {
    signals.push({ type: 'negative', label: 'Extended silence — deal at risk', impact: -30 });
  }

  if (activities.length === 0) {
    signals.push({ type: 'negative', label: 'Zero engagement detected', impact: -15 });
  }

  return signals;
}

function generateAutomatedActions(temperature: DealTemperature, daysWithoutResponse: number, activities: ClientActivity[]): AutomatedAction[] {
  const actions: AutomatedAction[] = [];

  if (temperature === 'hot') {
    actions.push({
      type: 'alert',
      label: 'High Probability Deal',
      description: 'Client showing strong buying signals. Recommend immediate engagement.',
      urgency: 'immediate',
      triggered: true,
    });
    actions.push({
      type: 'recommend_call',
      label: 'Recommend Immediate Call',
      description: 'Strong engagement detected — personal contact will accelerate close.',
      urgency: 'immediate',
      triggered: true,
    });
  }

  if (temperature === 'cold') {
    actions.push({
      type: 're_engage',
      label: 'Send Re-engagement Email',
      description: 'Low engagement detected. Trigger automated re-engagement sequence.',
      urgency: 'soon',
      triggered: daysWithoutResponse >= 5,
    });
    actions.push({
      type: 'flag_at_risk',
      label: 'Flag: At Risk',
      description: 'Deal health declining. Manual intervention recommended.',
      urgency: 'immediate',
      triggered: true,
    });
  }

  if (daysWithoutResponse >= 7 && temperature !== 'hot') {
    actions.push({
      type: 'escalate',
      label: 'Escalate to Management',
      description: 'No client response for 7+ days. Escalation recommended.',
      urgency: 'soon',
      triggered: true,
    });
  }

  return actions;
}

function generateRecommendation(temperature: DealTemperature, daysWithoutResponse: number, activities: ClientActivity[]): string {
  if (temperature === 'hot') {
    if (activities.some(a => a.type === 'revision_request')) {
      return 'Client requested revision — prepare updated proposal and call within 24h.';
    }
    return 'High probability deal — call client immediately to discuss next steps.';
  }

  if (temperature === 'warm') {
    if (daysWithoutResponse >= 3) {
      return 'Moderate engagement but response delayed. Send personalized check-in email.';
    }
    return 'Healthy engagement level. Continue standard follow-up schedule.';
  }

  // Cold
  if (daysWithoutResponse >= 10) {
    return 'Deal at critical risk. Consider escalation or alternative contact method.';
  }
  if (daysWithoutResponse >= 5) {
    return 'Low engagement detected. Trigger re-engagement email with value proposition.';
  }
  return 'Minimal client activity. Monitor and prepare re-engagement strategy.';
}

// ─── TEMPERATURE STYLES ──────────────────────────────────────────────────────

export const TEMPERATURE_STYLES: Record<DealTemperature, {
  bg: string; text: string; border: string; label: string; icon: string; ring: string; gradient: string;
}> = {
  hot: {
    bg: 'bg-red-500/15',
    text: 'text-red-300',
    border: 'border-red-500/40',
    label: 'HOT',
    icon: '🔥',
    ring: 'ring-red-500/30',
    gradient: 'from-red-500/20 to-orange-500/10',
  },
  warm: {
    bg: 'bg-amber-500/15',
    text: 'text-amber-300',
    border: 'border-amber-500/40',
    label: 'WARM',
    icon: '☀️',
    ring: 'ring-amber-500/30',
    gradient: 'from-amber-500/20 to-yellow-500/10',
  },
  cold: {
    bg: 'bg-blue-500/15',
    text: 'text-blue-300',
    border: 'border-blue-500/40',
    label: 'COLD',
    icon: '❄️',
    ring: 'ring-blue-500/30',
    gradient: 'from-blue-500/20 to-cyan-500/10',
  },
};

// ─── MOCK DATA GENERATOR ─────────────────────────────────────────────────────

function generateId(): string {
  return `ACT-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
}

function daysAgo(d: number): string {
  const date = new Date();
  date.setDate(date.getDate() - d);
  return date.toISOString();
}

function generateActivities(profile: 'hot' | 'warm' | 'cold', rfqId: string): ClientActivity[] {
  const activities: ClientActivity[] = [];

  if (profile === 'hot') {
    // Heavy engagement
    activities.push(
      { id: generateId(), rfqId, type: 'email_opened', timestamp: daysAgo(5), source: 'email' },
      { id: generateId(), rfqId, type: 'email_opened', timestamp: daysAgo(3), source: 'email' },
      { id: generateId(), rfqId, type: 'portal_visit', timestamp: daysAgo(4), source: 'portal' },
      { id: generateId(), rfqId, type: 'portal_visit', timestamp: daysAgo(2), source: 'portal' },
      { id: generateId(), rfqId, type: 'proposal_viewed', timestamp: daysAgo(4), source: 'portal' },
      { id: generateId(), rfqId, type: 'document_download', timestamp: daysAgo(3), source: 'portal' },
      { id: generateId(), rfqId, type: 'revision_request', timestamp: daysAgo(1), source: 'portal', details: 'Updated pressure requirements' },
      { id: generateId(), rfqId, type: 'response_sent', timestamp: daysAgo(4), source: 'email' },
    );
  } else if (profile === 'warm') {
    // Moderate engagement
    activities.push(
      { id: generateId(), rfqId, type: 'email_opened', timestamp: daysAgo(6), source: 'email' },
      { id: generateId(), rfqId, type: 'portal_visit', timestamp: daysAgo(5), source: 'portal' },
      { id: generateId(), rfqId, type: 'proposal_viewed', timestamp: daysAgo(5), source: 'portal' },
      { id: generateId(), rfqId, type: 'button_click', timestamp: daysAgo(4), source: 'portal', details: 'Clicked "View Equipment List"' },
    );
  } else {
    // Low/no engagement
    activities.push(
      { id: generateId(), rfqId, type: 'email_opened', timestamp: daysAgo(12), source: 'email' },
    );
  }

  return activities;
}

export function generateMockDealIntelligence(): DealIntelligenceRecord[] {
  const deals: DealIntelligenceRecord[] = [
    // ─── HOT DEALS ────────────────────────────────────────────────────────
    {
      rfqId: 'RFQ-001',
      referenceId: 'EPI-2026-0001',
      clientName: 'Ahmed Al-Rashid',
      clientEmail: 'a.rashid@aramco-ops.com',
      companyName: 'Saudi Aramco Operations',
      serviceName: 'Surface Well Testing',
      proposalSentDate: daysAgo(6),
      rfqScore: 92,
      dealHealthScore: 0, // Will be calculated
      temperature: 'hot',
      lastActivityDate: daysAgo(1),
      lastActivityType: 'revision_request',
      activityFrequency: 5.2,
      activities: generateActivities('hot', 'RFQ-001'),
      interventions: [],
      signals: [],
      automatedActions: [],
      recommendation: '',
      daysWithoutResponse: 1,
      status: 'active',
    },
    {
      rfqId: 'RFQ-004',
      referenceId: 'EPI-2026-0004',
      clientName: 'Omar Fadel',
      clientEmail: 'o.fadel@adnoc-eng.ae',
      companyName: 'ADNOC Engineering',
      serviceName: 'Stimulation & Nitrogen',
      proposalSentDate: daysAgo(5),
      rfqScore: 85,
      dealHealthScore: 0,
      temperature: 'hot',
      lastActivityDate: daysAgo(1),
      lastActivityType: 'portal_visit',
      activityFrequency: 4.8,
      activities: generateActivities('hot', 'RFQ-004'),
      interventions: [
        { id: 'INT-001', rfqId: 'RFQ-004', type: 'call_client', performedBy: 'Sales Manager', performedAt: daysAgo(2), notes: 'Client confirmed interest, awaiting internal budget approval' }
      ],
      signals: [],
      automatedActions: [],
      recommendation: '',
      daysWithoutResponse: 1,
      status: 'active',
    },
    // ─── WARM DEALS ────────────────────────────────────────────────────────
    {
      rfqId: 'RFQ-002',
      referenceId: 'EPI-2026-0002',
      clientName: 'Sarah Mitchell',
      clientEmail: 's.mitchell@petrofac.com',
      companyName: 'Petrofac UAE',
      serviceName: 'Engineering & Fabrication',
      proposalSentDate: daysAgo(8),
      rfqScore: 72,
      dealHealthScore: 0,
      temperature: 'warm',
      lastActivityDate: daysAgo(4),
      lastActivityType: 'button_click',
      activityFrequency: 2.1,
      activities: generateActivities('warm', 'RFQ-002'),
      interventions: [],
      signals: [],
      automatedActions: [],
      recommendation: '',
      daysWithoutResponse: 4,
      status: 'active',
    },
    {
      rfqId: 'RFQ-005',
      referenceId: 'EPI-2026-0005',
      clientName: 'Khalid Mostafa',
      clientEmail: 'k.mostafa@sapmt.com',
      companyName: 'SAPMT Construction',
      serviceName: 'Equipment Rental',
      proposalSentDate: daysAgo(7),
      rfqScore: 58,
      dealHealthScore: 0,
      temperature: 'warm',
      lastActivityDate: daysAgo(5),
      lastActivityType: 'portal_visit',
      activityFrequency: 1.8,
      activities: generateActivities('warm', 'RFQ-005'),
      interventions: [],
      signals: [],
      automatedActions: [],
      recommendation: '',
      daysWithoutResponse: 5,
      status: 'active',
    },
    {
      rfqId: 'RFQ-007',
      referenceId: 'EPI-2026-0007',
      clientName: 'Youssef Hamdan',
      clientEmail: 'y.hamdan@enppi.com',
      companyName: 'ENPPI Egypt',
      serviceName: 'Filtration & Water Treatment',
      proposalSentDate: daysAgo(6),
      rfqScore: 65,
      dealHealthScore: 0,
      temperature: 'warm',
      lastActivityDate: daysAgo(3),
      lastActivityType: 'email_opened',
      activityFrequency: 2.5,
      activities: generateActivities('warm', 'RFQ-007'),
      interventions: [],
      signals: [],
      automatedActions: [],
      recommendation: '',
      daysWithoutResponse: 3,
      status: 'active',
    },
    // ─── COLD DEALS ────────────────────────────────────────────────────────
    {
      rfqId: 'RFQ-003',
      referenceId: 'EPI-2026-0003',
      clientName: 'Fatima Al-Zahra',
      clientEmail: 'f.zahra@npcc.ae',
      companyName: 'NPCC Abu Dhabi',
      serviceName: 'Chemicals Supply',
      proposalSentDate: daysAgo(14),
      rfqScore: 45,
      dealHealthScore: 0,
      temperature: 'cold',
      lastActivityDate: daysAgo(12),
      lastActivityType: 'email_opened',
      activityFrequency: 0.3,
      activities: generateActivities('cold', 'RFQ-003'),
      interventions: [],
      signals: [],
      automatedActions: [],
      recommendation: '',
      daysWithoutResponse: 12,
      status: 'active',
    },
    {
      rfqId: 'RFQ-006',
      referenceId: 'EPI-2026-0006',
      clientName: 'Tariq Rahman',
      clientEmail: 't.rahman@worleyparsons.com',
      companyName: 'Worley Parsons',
      serviceName: 'Logistics & Transportation',
      proposalSentDate: daysAgo(16),
      rfqScore: 38,
      dealHealthScore: 0,
      temperature: 'cold',
      lastActivityDate: daysAgo(14),
      lastActivityType: 'email_opened',
      activityFrequency: 0.2,
      activities: generateActivities('cold', 'RFQ-006'),
      interventions: [],
      signals: [],
      automatedActions: [],
      recommendation: '',
      daysWithoutResponse: 14,
      status: 'active',
    },
    {
      rfqId: 'RFQ-008',
      referenceId: 'EPI-2026-0008',
      clientName: 'Nadia Habib',
      clientEmail: 'n.habib@gasco-uae.ae',
      companyName: 'GASCO UAE',
      serviceName: 'Surface Well Testing',
      proposalSentDate: daysAgo(11),
      rfqScore: 42,
      dealHealthScore: 0,
      temperature: 'cold',
      lastActivityDate: daysAgo(9),
      lastActivityType: 'email_opened',
      activityFrequency: 0.4,
      activities: generateActivities('cold', 'RFQ-008'),
      interventions: [],
      signals: [],
      automatedActions: [],
      recommendation: '',
      daysWithoutResponse: 9,
      status: 'active',
    },
  ];

  // Calculate deal health for each record
  return deals.map(deal => {
    const result = calculateDealHealth({
      rfqId: deal.rfqId,
      rfqScore: deal.rfqScore,
      activities: deal.activities,
      proposalSentDate: deal.proposalSentDate,
      lastResponseDate: deal.activities.find(a => a.type === 'response_sent')?.timestamp,
      daysWithoutResponse: deal.daysWithoutResponse,
    });

    return {
      ...deal,
      dealHealthScore: result.score,
      temperature: result.temperature,
      signals: result.signals,
      automatedActions: result.automatedActions,
      recommendation: result.recommendation,
      activityFrequency: result.activityFrequency,
    };
  });
}

// ─── INTERVENTION HELPERS ────────────────────────────────────────────────────

export function recordIntervention(
  deal: DealIntelligenceRecord,
  type: InterventionType,
  performedBy: string,
  notes?: string
): DealIntelligenceRecord {
  const intervention: InterventionRecord = {
    id: `INT-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
    rfqId: deal.rfqId,
    type,
    performedBy,
    performedAt: new Date().toISOString(),
    notes,
  };

  return {
    ...deal,
    interventions: [...deal.interventions, intervention],
  };
}

// ─── ACTIVITY TYPE LABELS ────────────────────────────────────────────────────

export const ACTIVITY_LABELS: Record<ActivityType, { label: string; icon: string }> = {
  email_opened: { label: 'Email Opened', icon: '📧' },
  portal_visit: { label: 'Portal Visit', icon: '🌐' },
  button_click: { label: 'Button Click', icon: '👆' },
  revision_request: { label: 'Revision Request', icon: '✏️' },
  document_download: { label: 'Document Download', icon: '📥' },
  response_sent: { label: 'Response Sent', icon: '💬' },
  proposal_viewed: { label: 'Proposal Viewed', icon: '👁️' },
};
